/**
 * DemoTour — lightweight guided tour overlay for the sandbox workspace.
 *
 * Only mounts when:
 *   • tenant.id starts with "demo-"
 *   • localStorage flag `ll_demo_tour_seen` is not set (first visit), OR the
 *     user explicitly re-launches via the demo-banner "Show tour" link.
 *
 * No external deps — pure React + CSS. Uses a dark veil with a transparent
 * spotlight box positioned via target element's getBoundingClientRect(), plus
 * a callout card anchored near the spotlight.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Sparkles, Rocket } from "lucide-react";

const STORAGE_KEY = "ll_demo_tour_seen";
const SPOTLIGHT_PAD = 10;  // px padding around the highlighted element

const STEPS = [
  {
    selector: null,  // centered intro modal (no spotlight)
    title: "Welcome to your demo workspace",
    body: "We've pre-loaded ~400 transactions across 12 months on 2 bank accounts, 6 live invoices (mix of paid/overdue/partial), 1 recurring template, and 1 active payment plan — including some Claude got slightly wrong, so you can experience the override flow. Take the 60-second tour?",
    placement: "center",
    cta: "Start tour",
  },
  {
    selector: '[data-testid="nav-dashboard"]',
    title: "1 / 5 · Dashboard",
    body: "Real-time P&L, cash-flow chart, and AI insights. Every number here is computed from the seeded transactions — no static images.",
    placement: "right",
  },
  {
    selector: '[data-testid="nav-transactions"]',
    title: "2 / 5 · Transactions",
    body: "Click here to see every line item. About 27% are marked 'Uncategorized' — try fixing one and watch the AI learn from your edit.",
    placement: "right",
  },
  {
    selector: '[data-testid="nav-statements"]',
    title: "3 / 5 · Upload a real statement",
    body: "Drag any CSV here when you're ready. You'll get a confirm-modal showing detected columns BEFORE we parse a single row.",
    placement: "right",
  },
  {
    selector: '[data-testid="nav-reports"]',
    title: "4 / 5 · GAAP-grade reports",
    body: "Multi-step Income Statement, Balance Sheet, and Reconciliation. One-click export to PDF or Excel — comparative columns included.",
    placement: "right",
  },
  {
    selector: '[data-testid="demo-banner-signup-btn"]',
    title: "5 / 5 · Loved it?",
    body: "When you're ready, click 'Sign up to save your work' to spin up your real workspace. Everything you saw here transfers seamlessly.",
    placement: "bottom",
    cta: "Finish tour",
  },
];

export default function DemoTour({ active }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const stepRef = useRef(0);

  // Boot: open on first visit if not dismissed
  useEffect(() => {
    if (!active) return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    // Slight delay so the dashboard finishes its initial render
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [active]);

  // Listen for the "show tour" trigger from the demo banner
  useEffect(() => {
    const onShow = () => { setIdx(0); setOpen(true); };
    window.addEventListener("ll:demo-tour:show", onShow);
    return () => window.removeEventListener("ll:demo-tour:show", onShow);
  }, []);

  // Recompute spotlight rect on every step / resize
  const computeRect = useCallback(() => {
    const step = STEPS[stepRef.current];
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - SPOTLIGHT_PAD,
      left: r.left - SPOTLIGHT_PAD,
      width: r.width + SPOTLIGHT_PAD * 2,
      height: r.height + SPOTLIGHT_PAD * 2,
    });
  }, []);

  useEffect(() => {
    stepRef.current = idx;
    if (!open) return;
    // Give the DOM a tick to settle (sidebar might be collapsed on mobile)
    const t = setTimeout(computeRect, 50);
    window.addEventListener("resize", computeRect);
    window.addEventListener("scroll", computeRect, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", computeRect);
      window.removeEventListener("scroll", computeRect, true);
    };
  }, [idx, open, computeRect]);

  if (!active || !open) return null;

  const step = STEPS[idx];
  const isFirst = idx === 0;
  const isLast = idx === STEPS.length - 1;

  const dismiss = (finished = false) => {
    if (finished) localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const next = () => {
    if (isLast) { dismiss(true); return; }
    setIdx((i) => i + 1);
  };
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  // Card positioning
  let cardStyle = {};
  if (step.placement === "center" || !rect) {
    cardStyle = {
      left: "50%", top: "50%", transform: "translate(-50%, -50%)",
      maxWidth: 460,
    };
  } else if (step.placement === "right") {
    cardStyle = {
      left: rect.left + rect.width + 18,
      top: Math.max(16, rect.top - 8),
      maxWidth: 340,
    };
  } else if (step.placement === "bottom") {
    cardStyle = {
      left: Math.max(16, rect.left),
      top: rect.top + rect.height + 18,
      maxWidth: 340,
    };
  }

  return (
    <div className="fixed inset-0 z-[200]" data-testid="demo-tour-overlay" aria-modal="true" role="dialog">
      {/* Veil + spotlight */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {rect ? (
          // Massive box-shadow trick: empty box at spotlight, shadow blacks-out everything else
          <div
            className="absolute transition-all duration-300 ease-out"
            style={{
              top: rect.top, left: rect.left, width: rect.width, height: rect.height,
              boxShadow: "0 0 0 9999px rgba(8, 28, 21, 0.78)",
              borderRadius: 8,
              outline: "2px solid var(--ll-ochre)",
              outlineOffset: 3,
            }}
            data-testid="demo-tour-spotlight"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "rgba(8, 28, 21, 0.78)" }} />
        )}
      </div>

      {/* Callout card */}
      <div
        className="absolute bg-white border shadow-2xl pointer-events-auto"
        style={{
          ...cardStyle,
          borderColor: "var(--ll-border)",
          animation: "tour-card-pop 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        data-testid={`demo-tour-card-${idx}`}
      >
        <div className="flex items-start gap-3 px-4 pt-4">
          <div className="w-8 h-8 grid place-items-center text-white shrink-0" style={{ background: "var(--ll-ink)" }}>
            {isLast ? <Rocket size={16} /> : <Sparkles size={16} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">
              {step.placement === "center" ? "Guided tour" : `Step ${idx} of ${STEPS.length - 1}`}
            </div>
            <div className="font-display font-bold text-base mt-0.5">{step.title}</div>
          </div>
          <button
            onClick={() => dismiss(true)}
            data-testid="demo-tour-close"
            className="p-1 -m-1 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] shrink-0"
            aria-label="Close tour"
          >
            <X size={14} />
          </button>
        </div>

        <p className="px-4 pt-2 pb-4 text-sm text-black/75 leading-relaxed">{step.body}</p>

        {/* Progress dots */}
        <div className="px-4 pb-3 flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className="h-1 transition-all"
              style={{
                width: i === idx ? 20 : 6,
                background: i <= idx ? "var(--ll-ink)" : "var(--ll-border)",
              }}
            />
          ))}
        </div>

        <div
          className="px-4 py-3 border-t flex items-center justify-between gap-2 bg-[var(--ll-bg-card)]"
          style={{ borderColor: "var(--ll-border)" }}
        >
          {!isFirst && !isLast ? (
            <button
              onClick={prev}
              data-testid="demo-tour-prev"
              className="text-xs uppercase tracking-widest px-2.5 py-1.5 hover:bg-white text-[var(--ll-ink-soft)] flex items-center gap-1"
            >
              <ChevronLeft size={12} /> Back
            </button>
          ) : (
            <button
              onClick={() => dismiss(true)}
              data-testid="demo-tour-skip"
              className="text-xs uppercase tracking-widest px-2.5 py-1.5 hover:bg-white text-[var(--ll-ink-soft)]"
            >
              Skip
            </button>
          )}
          <button
            onClick={next}
            data-testid="demo-tour-next"
            className="text-xs uppercase tracking-widest px-3 py-1.5 bg-[var(--ll-ink)] hover:bg-[#2D6A4F] text-white flex items-center gap-1"
          >
            {step.cta || (isLast ? "Done" : "Next")} {!isLast && <ChevronRight size={12} />}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes tour-card-pop {
          from { opacity: 0; transform: ${cardStyle.transform || "translateY(8px)"} scale(0.96); }
          to   { opacity: 1; transform: ${cardStyle.transform || "translateY(0)"} scale(1);     }
        }
      `}</style>
    </div>
  );
}
